import { ArrowLeft, ArrowRight, Check, EyeOff, LayoutDashboard, PartyPopper, RotateCcw, SkipForward, SquareTerminal } from '@orgus/ui/icons';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ONBOARDING_STEPS, isOnboardingStepId } from '@orgus/protocol';
import type { OnboardingStepId, OnboardingStepView } from '@orgus/protocol';
import { Badge, Button, ErrorState, IconButton, Pressable, Skeleton, cn, toUiError, toast } from '@orgus/ui';
import { NARROW_QUERY, useMediaQuery } from '../../lib/useMediaQuery.ts';
import type { UiError } from '@orgus/ui';
import { useConnection } from '../../lib/store.ts';
import { PageActions } from '../../components/shell/TopBar.tsx';
import { loadOnboarding, updateOnboarding, useOnboarding, useOnboardingData } from './client.ts';
import { JourneyLine, ProgressBar, StepStatusIcon } from './parts.tsx';
import { ChannelStep } from './steps/ChannelStep.tsx';
import { MachineStep } from './steps/MachineStep.tsx';
import { MissionStep } from './steps/MissionStep.tsx';
import { RepoStep } from './steps/RepoStep.tsx';
import { SignInStep } from './steps/SignInStep.tsx';
import { useOnboardingView } from './useOnboardingView.ts';

const STATUS_BADGE: Record<OnboardingStepView['status'], ReactNode> = {
  done: (
    <Badge tone="passed" icon={<Check />}>
      Done
    </Badge>
  ),
  skipped: <Badge icon={<SkipForward />}>Skipped</Badge>,
  current: (
    <Badge tone="accent" dot>
      Next up
    </Badge>
  ),
  todo: <Badge>To do</Badge>,
};

function StepList({ steps, selected, onSelect }: { steps: OnboardingStepView[]; selected: OnboardingStepId; onSelect: (id: OnboardingStepId) => void }) {
  return (
    <ol className="flex flex-col gap-1">
      {steps.map((s, i) => (
        <li key={s.id}>
          <Pressable
            onClick={() => onSelect(s.id)}
            aria-current={s.id === selected ? 'step' : undefined}
            className={cn(
              'flex w-full items-start gap-3 rounded-control px-3 py-2 text-left transition-colors hover:bg-hover',
              s.id === selected && 'bg-hover ring-1 ring-border',
            )}
          >
            <StepStatusIcon status={s.status} bare className="mt-1" />
            <span className="min-w-0 flex-1">
              <span className={cn('block text-ui', s.id === selected ? 'font-medium text-fg' : s.status === 'done' || s.status === 'skipped' ? 'text-fg-muted' : 'text-fg')}>
                <span className="text-fg-subtle num">{i + 1}.</span> {s.title}
              </span>
              <span className="block truncate text-caption text-fg-subtle">{s.detail}</span>
            </span>
          </Pressable>
        </li>
      ))}
    </ol>
  );
}

function Done({ onReview }: { onReview: () => void }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      <span className="flex size-12 items-center justify-center rounded-card bg-passed-soft text-passed ring-1 ring-passed/30" aria-hidden>
        <PartyPopper className="size-6" />
      </span>
      <p className="text-lead font-medium text-fg">You are set up</p>
      <p className="max-w-[52ch] text-ui text-fg-muted">
        Your machines, repos and sessions report here live. Overview shows what needs you first; your notification channel tells you when you are away. Getting started stays in Settings if you
        want it again.
      </p>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
        <Button variant="primary" icon={<LayoutDashboard />} onClick={() => navigate('/')}>
          Go to Overview
        </Button>
        <Button icon={<SquareTerminal />} onClick={() => navigate('/sessions')}>
          Open Sessions
        </Button>
        <Button variant="ghost" onClick={onReview}>
          Review the steps
        </Button>
      </div>
    </div>
  );
}

/** The five steps of the first ten minutes, one at a time, with progress saved on the hub. */
export function GettingStarted() {
  const navigate = useNavigate();
  const narrow = useMediaQuery(NARROW_QUERY);
  const [params, setParams] = useSearchParams();
  /** What a step change or hiding the checklist failed with: shown above the step, never a toast. */
  const [actionError, setActionError] = useState<UiError | null>(null);
  const { phase: dataPhase, mode } = useConnection();
  useOnboardingData({ templates: true, reach: true, pollMs: 15_000 });
  const view = useOnboardingView();
  const state = useOnboarding((s) => s.state);
  const loadError = useOnboarding((s) => s.error);
  // Hiding the checklist hides it for everyone on this hub, so viewers cannot.
  const canHide = useOnboarding((s) => s.probes.auth?.me?.role !== 'viewer');

  const asked = params.get('step');
  const selected: OnboardingStepId | 'done' = isOnboardingStepId(asked) ? asked : view.complete ? 'done' : (view.current ?? 'signin');
  const select = (id: OnboardingStepId | 'done') => setParams(id === 'done' ? {} : { step: id }, { replace: true });

  // Opening Getting started counts as starting it: the checklist then stays until done or hidden.
  const started = useRef(false);
  useEffect(() => {
    if (started.current || view.phase !== 'ready' || state.startedAt != null) return;
    started.current = true;
    void updateOnboarding({ op: 'start' }).catch(() => undefined);
  }, [view.phase, state.startedAt, mode]);

  const idx = selected === 'done' ? -1 : ONBOARDING_STEPS.findIndex((s) => s.id === selected);
  const step = idx >= 0 ? view.steps[idx]! : null;
  const prev = idx > 0 ? ONBOARDING_STEPS[idx - 1]!.id : null;
  const nextUndone = view.steps.find((s, i) => i > idx && (s.status === 'current' || s.status === 'todo'))?.id ?? null;
  const next = idx >= 0 && idx < ONBOARDING_STEPS.length - 1 ? ONBOARDING_STEPS[idx + 1]!.id : null;

  const mark = async (id: OnboardingStepId, how: 'done' | 'skipped' | null) => {
    try {
      setActionError(null);
      await updateOnboarding(how ? { op: 'mark', step: id, how } : { op: 'unmark', step: id });
      if (how) select(view.steps.find((s) => s.id !== id && (s.status === 'current' || s.status === 'todo'))?.id ?? 'done');
    } catch (e) {
      setActionError(toUiError(e));
    }
  };

  const hide = async () => {
    try {
      await updateOnboarding({ op: 'dismiss' });
      toast('Checklist hidden', {
        description: 'Bring it back from Settings, Getting started.',
        // The checklist also comes back from Settings, Getting started, so Undo has no time limit.
        action: { label: 'Undo', onClick: () => void updateOnboarding({ op: 'reopen' }).catch(() => navigate('/settings/getting-started')) },
      });
      navigate('/');
    } catch (e) {
      setActionError(toUiError(e));
    }
  };

  if (dataPhase === 'loading' || (view.phase === 'loading' && !loadError)) {
    return (
      <div className="flex flex-col gap-6 lg:flex-row" role="status" aria-label="Loading Getting started">
        <div className="flex flex-col gap-2 lg:w-66 lg:shrink-0">
          <Skeleton className="h-4 w-40" />
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-11" />
          ))}
        </div>
        <Skeleton className="h-105 min-w-0 flex-1" />
      </div>
    );
  }
  if (view.phase === 'error' && loadError) {
    return (
      <div className="rounded-card border border-border bg-surface">
        <ErrorState error={loadError} onRetry={() => loadOnboarding()} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
      <PageActions>
        {state.dismissedAt == null && !view.complete && canHide && (
          <Button variant="ghost" size="sm" icon={<EyeOff />} onClick={hide} className="hidden md:inline-flex">
            Hide checklist
          </Button>
        )}
      </PageActions>

      <aside className="flex flex-col gap-4 lg:sticky lg:top-6 lg:w-66 lg:shrink-0" aria-label="Getting started steps">
        <div className="flex flex-col gap-2 px-1">
          <p className="text-caption text-fg-subtle">Progress is saved as you go.</p>
          <ProgressBar done={view.done} total={view.total} />
          <JourneyLine state={state} />
        </div>
        {/* On a phone the progress bar and the step's own Back and Continue are the way through; the list would fill the first screen. */}
        <div className="max-lg:hidden">
          <StepList steps={view.steps} selected={selected === 'done' ? ('' as OnboardingStepId) : selected} onSelect={select} />
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col gap-3">
        {actionError && (
          <ErrorState variant="inline" error={actionError} action={<Button variant="ghost" size="sm" onClick={() => setActionError(null)}>Dismiss</Button>} />
        )}
        <section className="min-w-0 rounded-card border border-border bg-surface hairline" aria-labelledby="onboarding-step-title">
          {step ? (
            <>
              <header className="flex items-start gap-3 border-b border-border-subtle px-4 py-4">
                <div className="min-w-0 flex-1">
                  <p className="text-caption text-fg-subtle num">
                    Step {idx + 1} of {view.total}
                  </p>
                  <h2 id="onboarding-step-title" className="text-lead text-fg">
                    {step.title}
                  </h2>
                  <p className="text-ui text-fg-muted">{step.why}</p>
                </div>
                {STATUS_BADGE[step.status]}
              </header>
              <div className="px-4 py-4">
                {step.id === 'signin' ? (
                  <SignInStep />
                ) : step.id === 'machine' ? (
                  <MachineStep />
                ) : step.id === 'repo' ? (
                  <RepoStep />
                ) : step.id === 'mission' ? (
                  <MissionStep onConnectRepo={() => select('repo')} />
                ) : (
                  <ChannelStep />
                )}
              </div>
              {/* One row of actions on a phone too: Back folds to its arrow, Skip for now to Skip. */}
              <footer className="flex items-center gap-2 border-t border-border-subtle px-4 py-3">
                {prev &&
                  (narrow ? (
                    <IconButton size="sm" label="Back" icon={<ArrowLeft />} onClick={() => select(prev)} />
                  ) : (
                    <Button variant="ghost" size="sm" icon={<ArrowLeft />} onClick={() => select(prev)}>
                      Back
                    </Button>
                  ))}
                <span className="flex-1" />
                {step.skippable && step.status === 'skipped' && (
                  <Button variant="ghost" size="sm" icon={<RotateCcw />} onClick={() => mark(step.id, null)}>
                    Undo skip
                  </Button>
                )}
                {step.skippable && (step.status === 'current' || step.status === 'todo') && (
                  <Button variant="ghost" size="sm" icon={narrow ? undefined : <SkipForward />} onClick={() => mark(step.id, 'skipped')}>
                    {narrow ? 'Skip' : 'Skip for now'}
                  </Button>
                )}
                {step.id === 'phone' && step.status !== 'done' && (
                  <Button size="sm" variant="primary" icon={<Check />} onClick={() => mark('phone', 'done')}>
                    Mark as set up
                  </Button>
                )}
                {step.status === 'done' || step.status === 'skipped' || step.id === 'signin' ? (
                  nextUndone || next ? (
                    // The mission step has its own primary action (Start mission, then Open), so Continue steps back.
                    <Button variant={step.id === 'mission' ? 'secondary' : 'primary'} size="sm" iconRight={<ArrowRight />} onClick={() => select((nextUndone ?? next)!)}>
                      Continue
                    </Button>
                  ) : (
                    <Button variant="primary" size="sm" iconRight={<ArrowRight />} onClick={() => select('done')}>
                      Finish
                    </Button>
                  )
                ) : next ? (
                  <Button size="sm" iconRight={<ArrowRight />} onClick={() => select(next)}>
                    Next step
                  </Button>
                ) : null}
              </footer>
            </>
          ) : (
            <Done onReview={() => select('signin')} />
          )}
        </section>
      </div>
    </div>
  );
}
